import React, { useState, useCallback } from 'react';
import Layout from '../../components/layout';
import PageContainer from '../../components/PageContainer';
import { motion } from "framer-motion";
import { ArrowRight, Loader2, X } from "lucide-react";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import idt from "./Deeptech.png";
import magazine from "./Magazine1.png";
import { baseUrl } from '../../../Config/config';
import { validateEmail, isRestrictedEmail, RESTRICTED_ACCESS_MESSAGE } from '../../utils/emailValidation';
import RestrictedAccessModal from '../../components/RestrictedAccessModal';
import VIAssistantPreview from '../../components/VIAssistantPreview';

const IndiaDTReportInner = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formData, setFormData] = useState({
    name: '',
    firm_name: '',
    designation: '',
    email: '',
    city: '',
    country: '',
    phone: '',
    firm_type: '',
    receive_directory: '',
    about_our_service: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [restricted, setRestricted] = useState('');
  const [restrictedOpen, setRestrictedOpen] = useState(false);

  // The email field carries whichever message is currently blocking the submit,
  // so the disabled button always has a visible reason next to it - including
  // after the restricted-access modal has been dismissed.
  const emailFieldError = emailError || restricted;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'email') {
      if (emailError) setEmailError('');
      if (restricted) setRestricted('');
    }
  };

  const handleEmailBlur = () => {
    setEmailError(formData.email.trim() ? validateEmail(formData.email) : '');
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (restricted) return;

    const emailValidationError = validateEmail(formData.email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      setError('');
      return;
    }

    if (!executeRecaptcha) {
      setError('reCAPTCHA not ready, please try again.');
      return;
    }

    setLoading(true);
    setError('');

    let recaptchaToken;
    try {
      recaptchaToken = await executeRecaptcha('idt_report');
    } catch {
      setLoading(false);
      setError('reCAPTCHA check failed. Please try again.');
      return;
    }

    try {
      const jsonData = {
        ...formData,
        recaptcha: recaptchaToken
      };

      const response = await fetch(`${baseUrl}/iDTReport`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(jsonData),
      });

      // On success the server streams the report PDF itself; every other
      // outcome (errors, restricted email) is JSON.
      const isPdf = (response.headers.get('Content-Type') || '').includes('application/pdf');

      if (response.ok && isPdf) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "India-Deep-Tech-Report.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);

        setSuccess(true);
        setShowForm(false);
        return;
      }

      const data = await response.json().catch(() => ({}));

      if (!response.ok) throw new Error(data.message || 'Failed to download PDF');

      // The server records the submission either way and returns eligible:false
      // when the report must be withheld. isRestrictedEmail() is only a fallback
      // for a backend that predates the flag - safe to drop once it is live - so
      // it must not override an explicit eligible:true.
      if (data.eligible === false
        || (data.eligible === undefined && isRestrictedEmail(formData.email))) {
        setShowForm(false);
        setRestricted(data.message || RESTRICTED_ACCESS_MESSAGE);
        setRestrictedOpen(true);
        return;
      }

      throw new Error(data.message || 'Failed to download the report');
      
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [executeRecaptcha, formData, restricted]);

  const formFields = [
    { name: 'name', type: 'text', label: 'Your Name' },
    { name: 'firm_name', type: 'text', label: 'Firm Name' },
    { name: 'designation', type: 'text', label: 'Designation' },
    { name: 'email', type: 'email', label: 'Email Id' },
    { name: 'city', type: 'text', label: 'City' },
    { name: 'country', type: 'text', label: 'Country' },
    { name: 'phone', type: 'text', label: 'Phone' },
  ];

  const firmTypes = [
    { id: 'pevc-investor', label: 'PE/VC Investor' },
    { id: 'company-seeking-funding', label: 'Company Seeking Funding' },
    { id: 'advisory-firm', label: 'Advisory Firm' },
    { id: 'other', label: 'Other' }
  ];

  const directoryOptions = [
    { id: 'yes', label: 'Yes' },
    { id: 'no', label: 'No' }
  ];

  return (
    <Layout>
      <PageContainer
        title="India AI & Deep Tech Investment Report 2025 | IDTA & Venture Intelligence"
        description="Explore the India AI & Deep Tech Investment Report – trends, data, and deep insights into AI and deep tech investments in India 2025."
        additionalMetaTags={[
          { property: "og:type", content: "website" },
          { property: "og:url", content: "https://www.ventureintelligence.com/india-ai-deep-tech-report-2025" },
          { property: "og:title", content: "India AI & Deep Tech Investment Report 2025 – IDTA & Venture Intelligence" },
          { property: "og:description", content: "India AI & Deep Tech Investment Report 2025: Explore momentum in AI and deep tech investments in India – trends, data, and insights." },
          { name: "keywords", content: "AI India, Deep Tech India, Artificial Intelligence Investments, Deep Tech Startups, AI Funding India, Deep Tech Report, Semiconductor India, Space Tech India, Robotics India, Med Tech India, Climate Tech India, VC Funding India, PE Deals, Startup Investments India, Deal Intelligence, Investment Reports India, IDTA, Venture Intelligence Reports, Early Stage Investing, Research Development Innovation Fund, Enterprise AI, Healthcare AI, Fintech AI" },
          { name: "robots", content: "index, follow" },
          { name: "title", content: "India AI & Deep Tech Investment Report 2025 – IDTA & Venture Intelligence" }
        ]}
      >
      <div className="min-h-screen">
        <div className="relative h-48 mb-12">
          <div 
            className="absolute inset-0 bg-cover lg:bg-center"
            style={{ 
              backgroundImage: `url(${idt})`
            }}
          >
          </div>
        </div>

        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="max-w-5xl mx-auto mb-8"
          >
            <h2 className="text-2xl font-semibold text-gray-800 mb-8 text-center">
              Executive Summary
            </h2>

            <div className="relative">
              <p className="text-base text-gray-700 mb-6 leading-relaxed text-justify">
                This report, compiled for IDTA by Venture Intelligence, shows that momentum is significantly increasing for AI and deep tech investments in India.
              </p>
              <p className="text-base text-gray-700 mb-6 leading-relaxed text-justify">
                In 2025, even as overall venture activity moderated, capital continued to flow decisively toward technology-led sectors. Total VC investment in India held steady at about $10 billion, but within that headline number, AI and deep tech sharply outperformed.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                AI Startups: The Standout Theme
              </h3>
              <div className="flex flex-col xl:flex-row xl:items-start xl:gap-8 mb-4">
                <div className="xl:flex-1 xl:pr-4">
                  <p className="text-base text-gray-700 mb-6 leading-relaxed text-justify">
                    AI startups emerged as the standout theme of the year. In 2025 alone, AI companies attracted 188 investments worth $1.2 billion, representing a 58% year-on-year increase in funding value. This surge reflects growing confidence in real-world adoption – across enterprise software, healthcare, financial services, cybersecurity, and industrial applications. Importantly, AI now accounts for about 12% of total VC funding, up from under 5% in 2020, representing significant growth.
                  </p>
                    <div className='flex flex-col xl:flex-row gap-6'>
                        <div>
                            <p className="text-base text-gray-700 mb-6 leading-relaxed text-justify">
                                Looking more broadly, deep tech investment has reached critical mass. Since 2016, India has seen nearly $28 billion invested across over 2,100 deep tech deals, with deep tech now representing 15% of total PE-VC activity, compared to just 4% a decade ago. This marks a fundamental shift in investor priorities, from consumer-led growth toward science, engineering, and IP-driven innovation.
                            </p>
                            
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">
                                Healthy Diversification Within Deep Tech
                            </h3>
                            <p className="text-base text-gray-700 leading-relaxed text-justify">
                                The report also highlights a healthy diversification within deep tech. While AI dominates, strong momentum is visible in space technologies, semiconductors, robotics, advanced manufacturing, med tech, energy, and climate technologies. Encouragingly, early-stage deep tech investments have more than doubled over the past five years, indicating a widening pipeline of future category leaders.
                            </p>
                        </div>
                        <div className="m-auto h-80 w-72 xl:w-80 xl:h-[340px] mt-0 flex-shrink-0">
                            <img 
                                src={magazine} 
                                alt="AI & Deep Tech" 
                                className="w-full h-full rounded-lg shadow-md object-cover cursor-pointer"
                                onClick={() => setShowForm(true)}
                            />
                        </div>
                    </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <p className="text-base text-gray-700 mb-6 leading-relaxed text-justify">
                Another important signal is the growing participation of private equity and growth-stage investors. Large investments into AI analytics, semiconductor design, and medtech platforms underscore rising confidence in scale-up potential. At the same time, the report is clear: growth capital remains a bottleneck, and the absence of large, specialist deep tech funds risks slowing India's transition from innovation to global leadership.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Policy Support as a Critical Enabler
              </h3>
              <div className="flex flex-col xl:flex-row xl:items-start xl:gap-8 mb-6">
                <div className="xl:flex-1 xl:pr-4">
                  <p className="text-base text-gray-700 mb-6 leading-relaxed text-justify">
                    Policy support is emerging as a critical enabler. The ₹1 lakh crore Research Development and Innovation Fund has the potential to be a game-changing catalyst, bridging the gap between research, commercialization, and venture scale, especially in long-gestation sectors.
                  </p>
                </div>
                {/* <div className="xl:w-80 xl:h-48 mt-0 xl:mt-0 flex-shrink-0">
                  <img 
                    src={image1} 
                    alt="Policy Support" 
                    className="w-full h-full rounded-lg shadow-md object-cover"
                  />
                </div> */}
              </div>
            </div>

            <div className="flex justify-center mt-8">
              <button
                onClick={() => setShowForm(true)}
                className="px-8 py-3 bg-[#353d8d] text-white font-semibold rounded-lg transition-colors duration-200 flex items-center"
              >
                Download the Report <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </div>
          </motion.div>
        </div>

        {/* Form Popup */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-xl shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-800">
                  Just complete the following details to download the report.
                </h3>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="p-6">
                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                    {formFields.map((field) => (
                      <div key={field.name} className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700 capitalize">
                          {field.label}*
                        </label>
                        <input
                          type={field.type}
                          name={field.name}
                          required
                          value={formData[field.name]}
                          onChange={handleChange}
                          onBlur={field.name === 'email' ? handleEmailBlur : undefined}
                          aria-invalid={field.name === 'email' && !!emailFieldError}
                          aria-describedby={field.name === 'email' && emailFieldError ? 'email-error' : undefined}
                          className={`block w-full rounded-md shadow-sm sm:text-sm px-4 py-2 border ${
                            field.name === 'email' && emailFieldError
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                              : 'border-gray-300 focus:border-[#c59c40] focus:ring-[#c59c40]'
                          }`}
                        />
                        {field.name === 'email' && emailFieldError && (
                          <p id="email-error" className="text-red-600 text-xs">{emailFieldError}</p>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Firm Type*
                    </label>
                    <div className="flex flex-nowrap overflow-x-auto gap-4 pb-2">
                      {firmTypes.map((type) => (
                        <div key={type.id} className="flex-shrink-0">
                          <div className="flex items-center h-12 bg-gray-50 rounded-lg px-4 hover:bg-gray-100 transition-colors whitespace-nowrap">
                            <input
                              type="radio"
                              id={type.id}
                              name="firm_type"
                              value={type.id}
                              onChange={handleChange}
                              required
                              className="focus:ring-[#c59c40] h-4 w-4 text-[#c59c40] border-gray-300"
                            />
                            <label htmlFor={type.id} className="ml-3 block text-sm font-medium text-gray-700 cursor-pointer">
                              {type.label}
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Would you also like to receive a Directory of Most Active VC Investors in India?*
                    </label>
                    <div className="flex gap-4">
                      {directoryOptions.map((option) => (
                        <div key={option.id} className="flex items-center h-12 bg-gray-50 rounded-lg px-4 hover:bg-gray-100 transition-colors">
                          <input
                            type="radio"
                            id={`directory-${option.id}`}
                            name="receive_directory"
                            value={option.id}
                            onChange={handleChange}
                            required
                            className="focus:ring-[#c59c40] h-4 w-4 text-[#c59c40] border-gray-300"
                          />
                          <label htmlFor={`directory-${option.id}`} className="ml-3 block text-sm font-medium text-gray-700 cursor-pointer">
                            {option.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div> */}

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      How did you learn about our service?*
                    </label>
                    <textarea
                      name="about_our_service"
                      required
                      value={formData.about_our_service}
                      onChange={handleChange}
                      rows={3} 
                      className="block w-[75%] rounded-md border-gray-300 shadow-sm focus:border-[#c59c40] focus:ring-[#c59c40] sm:text-sm px-4 py-2 border"
                    />
                  </div>

                  {error && (
                    <p className="text-red-600 text-sm text-center">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={loading || !!emailFieldError}
                    className="w-full max-w-md mx-auto flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#bc8b20] hover:bg-[#d6b15f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#c59c40] transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        Submit <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </button>

                  {success && (
                    <p className="text-green-600 text-sm text-center">
                      Downloaded successfully!
                    </p>
                  )}
                </form>
              </div>
            </motion.div>
          </div>
        )}
        {/* VI Assistant promo - a styled preview of the assistant's welcome
            screen, shown once the thank-you modal has been dismissed. */}
        {!showForm && success && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="relative bg-white rounded-xl shadow-lg max-w-6xl w-full max-h-[95vh] overflow-hidden"
            >
              <button
                onClick={() => {setSuccess(false); setShowForm(false)}}
                aria-label="Close"
                className="absolute top-3 right-3 z-10 text-white/60 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>

              <VIAssistantPreview />
            </motion.div>
          </div>
        )}
        <RestrictedAccessModal
          open={restrictedOpen}
          onClose={() => setRestrictedOpen(false)}
          message={restricted}
        />
      </div>
      </PageContainer>
    </Layout>
  );
};

const IndiaDTReport = () => (
  <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY}>
    <IndiaDTReportInner />
  </GoogleReCaptchaProvider>
);

export default IndiaDTReport;